import { useState, useRef, useEffect, useCallback } from 'react';
import { AUDIO_BASE_URL, fetchAudioTimings, hasBismillah, fetchBismillahAudio } from '../../../services/quranService';
import { BISMILLAH_VERSE } from '../../../constants/quranConstants';
import { isIOS as checkIsIOS, isAndroid as checkIsAndroid } from '../../../utils/platformDetection';

/*
TODO: Add back the following audio control features that were available in useQuranAudio:
- volume: number - Volume level
- handleVolumeChange: (value: number) => void - Volume control
- isMuted: boolean - Mute state
- toggleMute: () => void - Mute toggle
- repeatMode: string - Current repeat mode
- handleRepeatClick: () => void - Toggle repeat modes
- repeatingVerse: number | null - Currently repeating verse
- toggleRepeat: (index: number) => void - Toggle repeat for specific verse
- audioFiles: Array - List of audio files
*/

// This hook provides gapless audio playback using the Web Audio API.
const useGaplessQuranAudio = (selectedSurah, selectedReciter, currentVerse, setCurrentVerse, surahName, reciterName, markVerseAsRead, onNativeStateSync) => {
  // Detect iOS and native iOS
  const isIOS = useRef(
    typeof window !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
  const isNativeIOS = typeof window !== 'undefined' && !!window.webkit?.messageHandlers?.quranAudioHandler;
  
  // Detect Android and native Android
  const isAndroid = useRef(checkIsAndroid());
  const isNativeAndroid = typeof window !== 'undefined' && !!window.AndroidInterface && !!window.ummahtiIosBridge;
  
  // Use native audio if either iOS or Android native is available
  const isNativeAudio = isNativeIOS || isNativeAndroid;

  // State - declare all state variables at the top level
  const [audioContextInitialized, setAudioContextInitialized] = useState(isNativeAudio ? true : false);
  const [audioBuffers, setAudioBuffers] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioProgress, setAudioProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [audioFiles, setAudioFiles] = useState([]);
  const [nativeCurrentVerse, setNativeCurrentVerse] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const [hasBismillahFile, setHasBismillahFile] = useState(false);

  // Refs
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const nextStartTimeRef = useRef(0);
  const currentVerseStartTimeRef = useRef(0);
  const progressAnimationFrameRef = useRef(null);
  const shouldContinuePlayback = useRef(true);
  const scheduledSourcesRef = useRef([]);
  const unlockAudioRef = useRef(false);
  const prevReciterRef = useRef(selectedReciter);

  // Add syncWithNative method for external forced sync, with lock to prevent overlap
  const getStateLockRef = useRef(false);
  const lastStateEventTimeRef = useRef(0);
  const lastStateSyncTimeRef = useRef(0);
  const lastSurahIdRef = useRef(null);
  const lastReciterIdRef = useRef(null);
  const stateRef = useRef({
    isPlaying,
    audioProgress,
    audioDuration,
    nativeCurrentVerse,
    hasStarted
  });

  // Add bismillah cache ref
  const bismillahCache = useRef({});

  // Keep the state ref up to date
  useEffect(() => {
    stateRef.current = {
      isPlaying, 
      audioProgress, 
      audioDuration, 
      nativeCurrentVerse, 
      hasStarted
    };
  }, [isPlaying, audioProgress, audioDuration, nativeCurrentVerse, hasStarted]);

  const computeStateChanges = useCallback((state, currentState) => {
    // Compute state changes without triggering re-renders
    const stateChanges = {};
    if (!!state.isPlaying !== currentState.isPlaying) 
      stateChanges.isPlaying = !!state.isPlaying;
    
    if (state.audioProgress !== currentState.audioProgress) 
      stateChanges.audioProgress = state.audioProgress || 0;
    
    if (state.audioDuration !== currentState.audioDuration) 
      stateChanges.audioDuration = state.audioDuration || 0;
    
    const newVerseIndex = typeof state.currentVerseIndex === 'number' ? state.currentVerseIndex : 0;
    if (newVerseIndex !== currentState.nativeCurrentVerse) 
      stateChanges.nativeCurrentVerse = newVerseIndex;
    
    if ((!!state.isPlaying || state.hasStarted) !== currentState.hasStarted) 
      stateChanges.hasStarted = !!state.isPlaying || state.hasStarted;
    
    return stateChanges;
  }, []);

  const syncWithNative = useCallback(() => {
    if (!isNativeAudio || !window.ummahtiIosBridge?.getQuranAudioState) return;
    if (getStateLockRef.current) return; // Prevent overlapping requests
    
    // Prevent rapid getState calls (min 1s between calls)
    const now = Date.now();
    if (now - lastStateSyncTimeRef.current < 1000) return;
    lastStateSyncTimeRef.current = now;
    
    console.log('[SYNC] Calling getQuranAudioState');
    getStateLockRef.current = true;
    window.ummahtiIosBridge.getQuranAudioState().then(state => {
      getStateLockRef.current = false;
      if (!state) return;
      
      // Batch state updates to prevent multiple re-renders
      const currentState = stateRef.current;
      const stateChanges = computeStateChanges(state, currentState);
      
      // Only update state if something changed (prevents unnecessary re-renders)
      if (Object.keys(stateChanges).length > 0) {
        if (stateChanges.isPlaying !== undefined) setIsPlaying(stateChanges.isPlaying);
        if (stateChanges.audioProgress !== undefined) setAudioProgress(stateChanges.audioProgress);
        if (stateChanges.audioDuration !== undefined) setAudioDuration(stateChanges.audioDuration);
        if (stateChanges.nativeCurrentVerse !== undefined) setNativeCurrentVerse(stateChanges.nativeCurrentVerse);
        if (stateChanges.hasStarted !== undefined) setHasStarted(stateChanges.hasStarted);
      }
      
      // Only sync if needed and if the surahId exists
      if (typeof onNativeStateSync === 'function' && state.surahId && 
          (state.surahId !== lastSurahIdRef.current || state.reciterId !== lastReciterIdRef.current)) {
        onNativeStateSync({
          surahId: state.surahId,
          reciterId: state.reciterId,
          verseIndex: state.currentVerseIndex
        });
        lastSurahIdRef.current = state.surahId;
        lastReciterIdRef.current = state.reciterId;
      }
    }).catch(() => { getStateLockRef.current = false; });
  }, [isNativeAudio, onNativeStateSync]);

  // Throttled quranAudio-state event handler (500ms instead of 200ms)
  useEffect(() => {
    if (!isNativeIOS) return;
    const handleStateEvent = (event) => {
      const now = Date.now();
      if (now - lastStateEventTimeRef.current < 500) return; // More aggressive throttle (500ms)
      lastStateEventTimeRef.current = now;
      
      console.log('[EVENT] Received quranAudio-state event', event.detail);
      const state = event.detail;
      if (!state) return;
      
      // Batch state updates to prevent multiple re-renders
      const currentState = stateRef.current;
      const stateChanges = computeStateChanges(state, currentState);
      
      // Only update state if something changed (prevents unnecessary re-renders)
      if (Object.keys(stateChanges).length > 0) {
        if (stateChanges.isPlaying !== undefined) setIsPlaying(stateChanges.isPlaying);
        if (stateChanges.audioProgress !== undefined) setAudioProgress(stateChanges.audioProgress);
        if (stateChanges.audioDuration !== undefined) setAudioDuration(stateChanges.audioDuration);
        if (stateChanges.nativeCurrentVerse !== undefined) setNativeCurrentVerse(stateChanges.nativeCurrentVerse);
        if (stateChanges.hasStarted !== undefined) setHasStarted(stateChanges.hasStarted);
      }
      
      // Only sync if needed and if the surahId exists
      if (typeof onNativeStateSync === 'function' && state.surahId && 
          (state.surahId !== lastSurahIdRef.current || state.reciterId !== lastReciterIdRef.current)) {
        onNativeStateSync({
          surahId: state.surahId,
          reciterId: state.reciterId,
          verseIndex: state.currentVerseIndex
        });
        lastSurahIdRef.current = state.surahId;
        lastReciterIdRef.current = state.reciterId;
      }
    };
    window.addEventListener('quranAudio-state', handleStateEvent);
    return () => {
      window.removeEventListener('quranAudio-state', handleStateEvent);
    };
  }, [isNativeIOS, onNativeStateSync, computeStateChanges]);

  // Only one effect for initial sync, periodic sync, and cleanup - reduce frequency to every 10s
  useEffect(() => {
    if (!isNativeIOS || !window.ummahtiIosBridge?.getQuranAudioState) return;
    // Initial sync
    syncWithNative();
    // Periodic sync every 10s (was 5s)
    const syncInterval = setInterval(syncWithNative, 10000);
    return () => {
      clearInterval(syncInterval);
      if (debouncedSyncRef.current) clearTimeout(debouncedSyncRef.current);
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, [isNativeIOS, syncWithNative]);

  // Add debounced state sync (200ms debounce)
  const debouncedSyncRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  const debouncedSyncWithNative = useCallback(() => {
    if (debouncedSyncRef.current) {
      clearTimeout(debouncedSyncRef.current);
    }
    debouncedSyncRef.current = setTimeout(() => {
      syncWithNative();
      debouncedSyncRef.current = null;
    }, 200);
  }, [syncWithNative]);

  // Manual sync on page focus/visibility for native iOS (debounced)
  useEffect(() => {
    if (!isNativeIOS) return;
    const handleVisibilityOrFocus = () => {
      debouncedSyncWithNative();
    };
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
    };
  }, [isNativeIOS, debouncedSyncWithNative]);

  // Add error recovery
  const [syncError, setSyncError] = useState(false);
  const errorRetryCountRef = useRef(0);

  const handleSyncError = useCallback(() => {
    setSyncError(true);
    errorRetryCountRef.current++;
    
    // Exponential backoff for retries
    const retryDelay = Math.min(1000 * Math.pow(2, errorRetryCountRef.current - 1), 10000);
    
    if (errorRetryCountRef.current <= 3) {
      syncTimeoutRef.current = setTimeout(() => {
        syncWithNative();
      }, retryDelay);
    }
  }, [syncWithNative]);

  // Function declarations - move all function declarations to the top
  const clearSources = useCallback((stopNative = false) => {
    if (isNativeIOS) {
      if (stopNative && window.ummahtiIosBridge?.stopQuranAudio) {
        window.ummahtiIosBridge.stopQuranAudio();
        setIsPlaying(false);
        setAudioProgress(0);
      }
      return;
    }

    console.log('Clearing all sources...');
    shouldContinuePlayback.current = false;
    
    // Clear all scheduled sources
    scheduledSourcesRef.current.forEach(source => {
      try {
        source.stop();
        source.disconnect();
      } catch (e) {
        console.error('Error stopping scheduled source:', e);
      }
    });
    scheduledSourcesRef.current = [];

    // Clear current source
    if (sourceRef.current) {
      try {
        sourceRef.current.stop();
        sourceRef.current.disconnect();
        sourceRef.current = null;
      } catch (e) {
        console.error('Error stopping current source:', e);
      }
    }

    // Reset timing references
    nextStartTimeRef.current = 0;
    currentVerseStartTimeRef.current = 0;

    // Clear progress animation
    if (progressAnimationFrameRef.current) {
      cancelAnimationFrame(progressAnimationFrameRef.current);
      progressAnimationFrameRef.current = null;
    }
    setAudioProgress(0);
  }, [isNativeIOS]);

  const updateProgress = useCallback(() => {
    if (isNativeIOS) return;
    
    if (!audioContextRef.current || !currentVerseStartTimeRef.current || !audioDuration) return;
    
    const elapsed = audioContextRef.current.currentTime - currentVerseStartTimeRef.current;
    const progressPercentage = Math.min(100, (elapsed / audioDuration) * 100);
    
    // Only update progress if it changed by at least 1%
    if (Math.abs(progressPercentage - audioProgress) >= 1) {
      setAudioProgress(progressPercentage);
    }
    
    if (progressPercentage < 100 && isPlaying) {
      progressAnimationFrameRef.current = requestAnimationFrame(updateProgress);
    }
  }, [audioDuration, isPlaying, isNativeIOS, audioProgress]);

  const initializeAudioContext = useCallback(async () => {
    if (isNativeIOS) return true;
    
    console.log('Initializing AudioContext...');
    try {
      if (!audioContextRef.current) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContext();
        console.log('AudioContext created:', audioContextRef.current.state);

        if (isIOS.current && !unlockAudioRef.current) {
          const silentBuffer = audioContextRef.current.createBuffer(1, 1, 22050);
          const source = audioContextRef.current.createBufferSource();
          source.buffer = silentBuffer;
          source.connect(audioContextRef.current.destination);
          source.start(0);
          unlockAudioRef.current = true;
        }
      }

      if (audioContextRef.current.state === 'suspended') {
        console.log('Resuming suspended AudioContext...');
        await audioContextRef.current.resume();
        console.log('AudioContext resumed:', audioContextRef.current.state);
      }

      setAudioContextInitialized(true);
      return true;
    } catch (error) {
      console.error('Failed to initialize AudioContext:', error);
      return false;
    }
  }, [isNativeIOS]);

  const loadVerseBuffer = useCallback(async (verseIndex) => {
    if (isNativeAudio) return null;
    
    if (!audioFiles[verseIndex] || !audioContextRef.current || audioBuffers[verseIndex]) return;

    try {
      const file = audioFiles[verseIndex];
      let url;

      // Handle different URL formats
      if (file.url.startsWith('http')) {
        // Full URL
        url = file.url;
      } else if (file.url.includes('mirrors.quranicaudio.com')) {
        // Direct quranicaudio URL - ensure https://
        url = `https://${file.url.replace(/^\/+/, '')}`;
      } else {
        // Relative URL - use base URL
        url = `${AUDIO_BASE_URL}${file.url}`;
      }
      
      console.log('Fetching audio file:', url);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      const decoded = await audioContextRef.current.decodeAudioData(arrayBuffer);
      
      setAudioBuffers(prev => ({
        ...prev,
        [verseIndex]: decoded
      }));
      
      return decoded;
    } catch (error) {
      console.error('Error loading verse buffer:', error);
      // If first attempt fails and it's a quranicaudio URL, try everyayah CDN
      if (error.message.includes('404') && audioFiles[verseIndex].url.includes('mirrors.quranicaudio.com')) {
        try {
          const fallbackUrl = `https://everyayah.com/data/${audioFiles[verseIndex].url.split('/').pop()}`;
          console.log('Retrying with fallback URL:', fallbackUrl);
          
          const response = await fetch(fallbackUrl);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const arrayBuffer = await response.arrayBuffer();
          const decoded = await audioContextRef.current.decodeAudioData(arrayBuffer);
          
          setAudioBuffers(prev => ({
            ...prev,
            [verseIndex]: decoded
          }));
          
          return decoded;
        } catch (retryError) {
          console.error('Error on fallback attempt:', retryError);
        }
      }
      return null;
    }
  }, [audioFiles, audioBuffers, isNativeIOS]);

  // Add function to load bismillah audio
  const loadBismillahBuffer = useCallback(async () => {
    if (isNativeAudio) return null;
    
    // Check cache first
    if (bismillahCache.current[selectedReciter]) {
      return bismillahCache.current[selectedReciter];
    }
    
    try {
      // Try to get the bismillah audio from the API
      const bismillahData = await fetchBismillahAudio(selectedReciter);
      
      if (!bismillahData || !bismillahData.audio_file) {
        console.log('No bismillah audio file from API, trying direct URL construction');
        // Fallback to direct URL construction if the API fails
        return await loadBismillahBufferFallback();
      }
      
      let url = bismillahData.audio_file.url;
      
      // Handle different URL formats
      if (url.startsWith('http')) {
        // Full URL
      } else if (url.includes('mirrors.quranicaudio.com')) {
        // Direct quranicaudio URL - ensure https://
        url = `https://${url.replace(/^\/+/, '')}`;
      } else {
        // Relative URL - use base URL
        url = `${AUDIO_BASE_URL}${url}`;
      }
      
      console.log('Fetching bismillah audio file:', url);
      
      const response = await fetch(url);
      if (!response.ok) {
        console.log('Error fetching bismillah audio, trying fallback');
        return await loadBismillahBufferFallback();
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const decoded = await audioContextRef.current.decodeAudioData(arrayBuffer);
      
      // Cache the buffer
      bismillahCache.current[selectedReciter] = decoded;
      
      return decoded;
    } catch (error) {
      console.error('Error loading bismillah buffer:', error);
      // Try fallback if the API fails
      return await loadBismillahBufferFallback();
    }
  }, [selectedReciter, isNativeAudio]);

  // Add fallback method for loading bismillah
  const loadBismillahBufferFallback = useCallback(async () => {
    try {
      // Try constructing common URL patterns for bismillah audio
      const possibleUrls = [
        // Pattern 1: Download from quranicaudio.com with chapter/verse format
        `https://download.quranicaudio.com/quran/${selectedReciter}/001001.mp3`,
        // Pattern 2: Alternative format with underscore
        `https://download.quranicaudio.com/quran/${selectedReciter}/001_001.mp3`,
        // Pattern 3: Using everyayah.com as a fallback
        `https://everyayah.com/data/${selectedReciter}/001001.mp3`,
        // Pattern 4: Generic bismillah audio as last resort
        `https://everyayah.com/data/Abdul_Basit_Murattal_64kbps/001001.mp3`
      ];
      
      // Try each URL until one works
      for (const url of possibleUrls) {
        try {
          console.log('Trying fallback bismillah URL:', url);
          const response = await fetch(url);
          if (response.ok) {
            const arrayBuffer = await response.arrayBuffer();
            const decoded = await audioContextRef.current.decodeAudioData(arrayBuffer);
            
            // Cache the buffer
            bismillahCache.current[selectedReciter] = decoded;
            
            console.log('Successfully loaded bismillah audio from fallback URL:', url);
            return decoded;
          }
        } catch (e) {
          console.log('Fallback URL failed:', url);
          // Continue to the next URL
        }
      }
      
      console.error('All fallback URLs failed');
      return null;
    } catch (error) {
      console.error('Error in bismillah fallback:', error);
      return null;
    }
  }, [selectedReciter]);

  // Update scheduleVerses to use the new loadBismillahBuffer function
  const scheduleVerses = useCallback(async (startIndex, count = 2) => {
    if (isNativeIOS) return;
    
    if (!audioContextRef.current || !shouldContinuePlayback.current) return;

    const context = audioContextRef.current;
    const schedulingOffset = isIOS.current ? 0.1 : 0;
    let currentTime = Math.max(
      nextStartTimeRef.current || context.currentTime,
      context.currentTime + schedulingOffset
    );
    
    // Check if we need to play bismillah before the first verse
    if (hasBismillahFile && startIndex === 0) {
      try {
        console.log('Attempting to load bismillah audio for reciter:', selectedReciter);
        const bismillahBuffer = await loadBismillahBuffer();
        
        if (bismillahBuffer) {
          // Create and schedule bismillah source
          const bismillahSource = context.createBufferSource();
          bismillahSource.buffer = bismillahBuffer;
          
          const gainNode = context.createGain();
          bismillahSource.connect(gainNode);
          gainNode.connect(context.destination);
          
          console.log('Scheduling bismillah to play at:', currentTime);
          bismillahSource.start(currentTime);
          
          // Add small gap after bismillah before starting the first verse
          const bismillahDuration = bismillahBuffer.duration;
          currentTime += bismillahDuration + 0.1; // Small gap
          
          // Store this for cleanup
          scheduledSourcesRef.current.push(bismillahSource);
          console.log('Bismillah audio scheduled successfully');
        } else {
          console.log('No bismillah buffer available, continuing with regular verses');
        }
      } catch (error) {
        console.error('Error playing bismillah:', error);
        // Continue with normal playback if bismillah fails
      }
    }
    
    for (let i = 0; i < count && startIndex + i < audioFiles.length; i++) {
      const index = startIndex + i;
      
      let buffer = audioBuffers[index];
      if (!buffer) {
        buffer = await loadVerseBuffer(index);
        if (!buffer) continue;
      }

      const sourceNode = context.createBufferSource();
      sourceNode.buffer = buffer;
      
      const gainNode = context.createGain();
      sourceNode.connect(gainNode);
      gainNode.connect(context.destination);

      console.log(`Scheduling verse ${index} to start at:`, currentTime);
      sourceNode.start(currentTime);

      if (i === 0) {
        currentVerseStartTimeRef.current = currentTime;
        setAudioDuration(buffer.duration);
        sourceRef.current = sourceNode;
      } else {
        scheduledSourcesRef.current.push(sourceNode);
      }

      sourceNode.onended = () => {
        console.log(`Verse ${index} ended`);
        if (shouldContinuePlayback.current && index < audioFiles.length - 1) {
          setCurrentVerse(index + 1);
          if (i === count - 2) {
            setTimeout(() => {
              scheduleVerses(index + 2, 2);
            }, isIOS.current ? 50 : 0);
          }
        } else if (index === audioFiles.length - 1) {
          setIsPlaying(false);
          shouldContinuePlayback.current = false;
        }
      };

      currentTime += buffer.duration;
    }

    nextStartTimeRef.current = currentTime;
  }, [
    audioFiles, 
    audioBuffers, 
    loadVerseBuffer, 
    setCurrentVerse, 
    isNativeIOS, 
    hasBismillahFile, 
    loadBismillahBuffer, 
    selectedReciter
  ]);

  // Modified playVerse to handle bismillah
  const playVerse = useCallback(async (index) => {
    if (isNativeAudio) {
      if (!audioFiles.length) return;
      
      try {
        // Always send absolute URLs
        const absoluteUrls = audioFiles.map(f => {
          if (f.url.startsWith('http')) return f.url;
          return `${AUDIO_BASE_URL}${f.url}`;
        });
        
        // Check if we need to prepend the bismillah audio URL
        // For native audio, we instruct the native audio player to handle bismillah
        const shouldPlayBismillah = hasBismillahFile && index === 0;
        
        if (window.ummahtiIosBridge?.playQuranAudio) {
          await window.ummahtiIosBridge.playQuranAudio(
            absoluteUrls, 
            index, 
            surahName, 
            reciterName,
            shouldPlayBismillah
          );
        }
        
        setNativeCurrentVerse(index);
        setIsPlaying(true);
        setHasStarted(true);
        
        // Update last reading session when a verse is played
        if (markVerseAsRead && selectedSurah && typeof index === 'number') {
          markVerseAsRead(selectedSurah, `${selectedSurah}:${index + 1}`);
        }
      } catch (error) {
        console.error('[ERROR] Failed to play verse:', error);
        // Try to recover state
        debouncedSyncWithNative();
      }
      return;
    }
    
    console.log('Attempting to play verse:', index);
    
    if (!audioFiles[index]) {
      console.log('No audio file metadata for verse:', index);
      return;
    }

    if (!audioContextRef.current) {
      console.log('No AudioContext, initializing...');
      const initialized = await initializeAudioContext();
      if (!initialized) {
        console.error('Failed to initialize AudioContext');
        return;
      }
    }

    if (audioContextRef.current.state === 'suspended') {
      console.log('AudioContext suspended, resuming...');
      await audioContextRef.current.resume();
    }

    console.log('AudioContext state:', audioContextRef.current.state);
    
    // Ensure clean state before starting new playback
    clearSources();
    
    // Reset timing references
    nextStartTimeRef.current = audioContextRef.current.currentTime;
    shouldContinuePlayback.current = true;

    try {
      // Check if we should play bismillah first (it's the first verse and surah needs bismillah)
      const shouldPlayBismillah = hasBismillahFile && index === 0;
      
      if (shouldPlayBismillah) {
        console.log("Playing bismillah before first verse");
        // We'll rely on the scheduleVerses function to handle the playback sequence
      }
      
      await scheduleVerses(index);
      setCurrentVerse(index);
      setIsPlaying(true);
      progressAnimationFrameRef.current = requestAnimationFrame(updateProgress);
      // Update last reading session when a verse is played
      if (markVerseAsRead && selectedSurah && typeof index === 'number') {
        markVerseAsRead(selectedSurah, `${selectedSurah}:${index + 1}`);
      }
    } catch (error) {
      console.error('Error playing verse:', error);
      setIsPlaying(false);
      shouldContinuePlayback.current = false;
    }
  }, [audioFiles, clearSources, scheduleVerses, updateProgress, initializeAudioContext, setCurrentVerse, isNativeIOS, surahName, reciterName, markVerseAsRead, selectedSurah, debouncedSyncWithNative, hasBismillahFile]);

  // Modified togglePlay to use the cached handlers
  const togglePlay = useCallback(async () => {
    if (isNativeAudio) {
      try {
        if (!hasStarted) {
          // First play: send play command
          const verseIndex = typeof currentVerse === 'number' ? currentVerse : 0;
          await playVerse(verseIndex);
          return;
        }
        
        if (isPlaying) {
          if (window.ummahtiIosBridge?.pauseQuranAudio) {
            await window.ummahtiIosBridge.pauseQuranAudio();
          }
          setIsPlaying(false);
        } else {
          if (window.ummahtiIosBridge?.resumeQuranAudio) {
            await window.ummahtiIosBridge.resumeQuranAudio();
          }
          setIsPlaying(true);
        }
      } catch (error) {
        console.error('[ERROR] Failed to toggle play state:', error);
        // Try to recover state
        debouncedSyncWithNative();
      }
      return;
    }
    
    console.log('Toggle play called, current state:', { isPlaying });
    
    try {
      if (isPlaying) {
        clearSources();
        setIsPlaying(false);
      } else {
        if (isIOS.current && audioContextRef.current?.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        if (!audioContextRef.current) {
          const initialized = await initializeAudioContext();
          if (!initialized) {
            throw new Error('Failed to initialize AudioContext');
          }
        }

        shouldContinuePlayback.current = true;
        const verseIndex = typeof currentVerse === 'number' ? currentVerse : 0;
        await playVerse(verseIndex);
      }
    } catch (error) {
      console.error('Error in togglePlay:', error);
      setIsPlaying(false);
      shouldContinuePlayback.current = false;
      throw error;
    }
  }, [isPlaying, currentVerse, playVerse, clearSources, initializeAudioContext, isNativeAudio, hasStarted, debouncedSyncWithNative]);

  // Effects
  useEffect(() => {
    const fetchAudioFiles = async () => {
      if (!selectedSurah || !selectedReciter) return;
      
      // Stop any current playback and clear state
      clearSources(false);
      setIsPlaying(false);
      if (!isNativeAudio) {
        setAudioBuffers({});
      }
      
      setIsLoading(true);
      try {
        const audioData = await fetchAudioTimings(selectedReciter, selectedSurah);
        if (!audioData.audio_files || audioData.audio_files.length === 0) {
          setAudioFiles([]);
          setIsLoading(false);
          return;
        }

        // Check if we need to add bismillah audio
        const shouldHaveBismillah = hasBismillah(parseInt(selectedSurah));
        let filesList = [...audioData.audio_files];
        setHasBismillahFile(shouldHaveBismillah);
        
        if (shouldHaveBismillah && filesList.length > 0) {
          // For the native iOS player, we'll adjust the play method
          // For web playback, we'll keep the original files array
          console.log('This surah should have bismillah played before the first verse');
        }
        
        setAudioFiles(filesList);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching audio files:', error);
        setAudioFiles([]);
        setIsLoading(false);
      }
    };

    fetchAudioFiles();
  }, [selectedSurah, selectedReciter, clearSources, isNativeAudio]);

  // Add effect to clear audio context when reciter changes
  useEffect(() => {
    if (isNativeIOS) {
      // Only reset state if reciter has actually changed
      if (prevReciterRef.current !== selectedReciter) {
        setHasStarted(false);
        setIsPlaying(false);
        setAudioProgress(0);
        setAudioDuration(0);
        setNativeCurrentVerse(0);
        setAudioFiles([]);
      }
      prevReciterRef.current = selectedReciter;
      return;
    }
    if (isNativeIOS) return;
    
    // Clear audio context and buffers when reciter changes
    if (audioContextRef.current) {
      clearSources();
      setIsPlaying(false);
      setAudioBuffers({});
      audioContextRef.current.close().catch(console.error);
      audioContextRef.current = null;
      setAudioContextInitialized(false);
    }
  }, [selectedReciter, clearSources, isNativeIOS]);

  useEffect(() => {
    if (isNativeIOS || !isIOS.current) return;

    const handleVisibilityChange = () => {
      if (document.hidden && isPlaying) {
        clearSources();
        setIsPlaying(false);
      }
    };

    const handleFocus = async () => {
      if (audioContextRef.current?.state === 'suspended') {
        await audioContextRef.current.resume();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isPlaying, clearSources, isNativeIOS]);

  useEffect(() => {
    if (isNativeIOS) return;
    
    return () => {
      shouldContinuePlayback.current = false;
      clearSources();
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(console.error);
      }
    };
  }, [clearSources, isNativeIOS]);

  // Listen for native events when in native audio mode
  useEffect(() => {
    if (!isNativeAudio) return;
    
    const handleVerseChanged = (e) => {
      const { verseIndex, duration, currentVerseIndex } = e.detail || {};
      // Handle both verseIndex and currentVerseIndex for compatibility
      const index = typeof verseIndex === 'number' ? verseIndex : currentVerseIndex;
      if (typeof index === 'number') {
        setNativeCurrentVerse(index);
        setCurrentVerse(index);
        setAudioProgress(0);
        setAudioDuration(duration || 0);
      }
    };
    
    const handleProgress = (e) => {
      const { progress, duration } = e.detail || {};
      setAudioProgress(progress || 0);
      if (duration) setAudioDuration(duration);
    };
    
    const handlePlaybackEnded = () => {
      setIsPlaying(false);
      setAudioProgress(100);
    };
    
    // Add handler for paused event
    const handlePaused = () => {
      setIsPlaying(false);
    };
    
    // Add handler for state changes (used by Android)
    const handleState = (e) => {
      const state = e.detail || {};
      if (typeof state.isPlaying === 'boolean') {
        setIsPlaying(state.isPlaying);
      }
      if (typeof state.currentVerseIndex === 'number') {
        setNativeCurrentVerse(state.currentVerseIndex);
        setCurrentVerse(state.currentVerseIndex);
      }
      if (typeof state.audioProgress === 'number') {
        setAudioProgress(state.audioProgress);
      }
      if (typeof state.audioDuration === 'number') {
        setAudioDuration(state.audioDuration);
      }
    };
    
    const handleHandlerReady = () => {
      console.log('Native Quran audio handler is ready');
      // Any initialization code can go here
    };
    
    window.addEventListener('quranAudio-verseChanged', handleVerseChanged);
    window.addEventListener('quranAudio-progress', handleProgress);
    window.addEventListener('quranAudio-playbackEnded', handlePlaybackEnded);
    window.addEventListener('quranAudio-paused', handlePaused);
    window.addEventListener('quranAudio-state', handleState);
    window.addEventListener('quranAudio-handlerReady', handleHandlerReady);
    
    return () => {
      window.removeEventListener('quranAudio-verseChanged', handleVerseChanged);
      window.removeEventListener('quranAudio-progress', handleProgress);
      window.removeEventListener('quranAudio-playbackEnded', handlePlaybackEnded);
      window.removeEventListener('quranAudio-paused', handlePaused);
      window.removeEventListener('quranAudio-state', handleState);
      window.removeEventListener('quranAudio-handlerReady', handleHandlerReady);
    };
  }, [isNativeAudio, setCurrentVerse]);

  // Keep currentVerse in sync with prop for native audio
  useEffect(() => {
    if (!isNativeAudio) return;
    
    if (typeof currentVerse === 'number' && currentVerse !== nativeCurrentVerse) {
      setNativeCurrentVerse(currentVerse);
    }
  }, [currentVerse, nativeCurrentVerse, isNativeAudio]);

  // Add cleanup for error handling
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      if (debouncedSyncRef.current) {
        clearTimeout(debouncedSyncRef.current);
      }
    };
  }, []);

  return {
    isPlaying,
    audioBuffers: isNativeAudio ? {} : audioBuffers,
    playVerse,
    togglePlay,
    audioContext: isNativeAudio ? null : audioContextRef.current,
    audioProgress,
    audioDuration,
    audioContextInitialized,
    isLoading,
    clearSources,
    syncWithNative,
    syncError,
    hasBismillah: hasBismillahFile
  };
};

export default useGaplessQuranAudio; 